import { useQuery } from '@tanstack/react-query';
import { Building2, Users, Music, Mail, TrendingUp, MessageSquare } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { analyticsAPI } from '../../api/endpoints';
import type { DashboardAnalytics as DashboardAnalyticsType } from '../../types';
import { Skeleton } from './Skeleton';

const CHART_COLORS = {
  sent: '#0ea5e9',
  received: '#38bdf8',
  done: '#22c55e',
  in_discussion: '#f59e0b',
  canceled: '#ef4444',
  bar: '#0ea5e9',
  text: '#94a3b8',
  grid: '#334155',
};

const PIE_DATA_KEYS: { key: 'done' | 'in_discussion' | 'canceled'; label: string; color: string }[] = [
  { key: 'done', label: 'Booked', color: CHART_COLORS.done },
  { key: 'in_discussion', label: 'In Discussion', color: CHART_COLORS.in_discussion },
  { key: 'canceled', label: 'Canceled', color: CHART_COLORS.canceled },
];

function StatCard({ label, value, icon: Icon }: { label: string; value: number; icon: React.ElementType }) {
  return (
    <div className="p-4 bg-dark-elevated rounded-xl border border-dark-border">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary-900/50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary-400" />
        </div>
        <div>
          <p className="text-xl font-bold text-dark-text-primary">{value.toLocaleString()}</p>
          <p className="text-xs text-dark-text-secondary">{label}</p>
        </div>
      </div>
    </div>
  );
}

function PercentageCard({ label, value, description, icon: Icon }: { label: string; value: number; description: string; icon: React.ElementType }) {
  const percentage = Math.round(value * 100);
  return (
    <div className="p-5 bg-dark-elevated rounded-xl border border-dark-border">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-dark-text-secondary" />
        <h4 className="text-sm font-medium text-dark-text-secondary">{label}</h4>
      </div>
      <p className="text-3xl font-bold text-dark-text-primary mb-2">{percentage}%</p>
      <div className="w-full bg-dark-border rounded-full h-2 mb-2">
        <div
          className="bg-primary-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-dark-text-muted">{description}</p>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-dark-elevated border border-dark-border rounded-lg p-3 shadow-lg">
      <p className="text-sm text-dark-text-primary font-medium mb-1">{label}</p>
      {payload.map((entry: { name: string; value: number; color: string }) => (
        <p key={entry.name} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PieTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="bg-dark-elevated border border-dark-border rounded-lg p-3 shadow-lg">
      <p className="text-sm text-dark-text-primary font-medium">
        {entry.name}: {entry.value}
      </p>
    </div>
  );
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m) - 1]} ${year.slice(2)}`;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-4 bg-dark-elevated rounded-xl border border-dark-border">
            <div className="flex items-center gap-3">
              <Skeleton width="40px" height="40px" className="rounded-lg" />
              <div>
                <Skeleton width="60px" height="24px" className="mb-1" />
                <Skeleton width="80px" height="14px" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-dark-elevated rounded-xl border border-dark-border p-5">
          <Skeleton width="160px" height="20px" className="mb-4" />
          <Skeleton width="100%" height="200px" />
        </div>
        <div className="bg-dark-elevated rounded-xl border border-dark-border p-5">
          <Skeleton width="120px" height="20px" className="mb-4" />
          <Skeleton width="100%" height="200px" />
        </div>
      </div>
    </div>
  );
}

export function DashboardAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: async () => {
      const response = await analyticsAPI.getDashboard();
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-dark-text-primary mb-4">Dashboard</h2>
        <LoadingSkeleton />
      </div>
    );
  }

  if (!data) return null;

  const { summary, emails_over_time, top_contacted_venues, response_rate, booking_conversion, concert_status } = data as DashboardAnalyticsType;

  const chartData = emails_over_time.map((e) => ({
    month: formatMonth(e.month),
    Sent: e.sent,
    Received: e.received,
  }));

  const pieData = PIE_DATA_KEYS
    .map(({ key, label, color }) => ({
      name: label,
      value: concert_status[key],
      color,
    }))
    .filter((d) => d.value > 0);

  const totalConcerts = pieData.reduce((sum, d) => sum + d.value, 0);

  const venueChartData = top_contacted_venues.map((v) => ({
    name: v.venue_name.length > 20 ? v.venue_name.slice(0, 18) + '...' : v.venue_name,
    fullName: v.venue_name,
    communications: v.total_communications,
  }));

  const hasEmailData = emails_over_time.some((e) => e.sent > 0 || e.received > 0);

  return (
    <div>
      <h2 className="text-xl font-semibold text-dark-text-primary mb-4">Dashboard</h2>

      <div className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Venues" value={summary.total_venues} icon={Building2} />
          <StatCard label="Contacts" value={summary.total_contacts} icon={Users} />
          <StatCard label="Concerts Booked" value={summary.concerts_booked} icon={Music} />
          <StatCard label="Emails Sent" value={summary.emails_sent} icon={Mail} />
        </div>

        {/* Row 1: Emails Over Time + Concert Status */}
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 bg-dark-elevated rounded-xl border border-dark-border p-5">
            <h3 className="text-sm font-medium text-dark-text-secondary mb-4">Emails Over Time</h3>
            {hasEmailData ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                  <XAxis dataKey="month" tick={{ fill: CHART_COLORS.text, fontSize: 11 }} />
                  <YAxis tick={{ fill: CHART_COLORS.text, fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: CHART_COLORS.text }} />
                  <Bar dataKey="Sent" fill={CHART_COLORS.sent} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Received" fill={CHART_COLORS.received} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-dark-text-muted text-sm">
                No email data yet
              </div>
            )}
          </div>

          <div className="bg-dark-elevated rounded-xl border border-dark-border p-5">
            <h3 className="text-sm font-medium text-dark-text-secondary mb-4">Concert Status</h3>
            {totalConcerts > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: CHART_COLORS.text }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-dark-text-muted text-sm">
                No concerts yet
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Top Venues + Rate Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dark-elevated rounded-xl border border-dark-border p-5">
            <h3 className="text-sm font-medium text-dark-text-secondary mb-4">Top Contacted Venues</h3>
            {venueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, venueChartData.length * 36)}>
                <BarChart data={venueChartData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} horizontal={false} />
                  <XAxis type="number" tick={{ fill: CHART_COLORS.text, fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={130}
                    tick={{ fill: CHART_COLORS.text, fontSize: 11 }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-dark-elevated border border-dark-border rounded-lg p-3 shadow-lg">
                          <p className="text-sm text-dark-text-primary font-medium">{d.fullName}</p>
                          <p className="text-xs text-primary-400">{d.communications} communications</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="communications" fill={CHART_COLORS.bar} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-dark-text-muted text-sm">
                No venue data yet
              </div>
            )}
          </div>

          <div className="space-y-4">
            <PercentageCard
              label="Response Rate"
              value={response_rate}
              description={`Threads where you sent and received a reply`}
              icon={MessageSquare}
            />
            <PercentageCard
              label="Booking Conversion"
              value={booking_conversion.rate}
              description={`${booking_conversion.concerts_booked} concerts from ${booking_conversion.venues_contacted} contacted venues`}
              icon={TrendingUp}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
